import { useWallet } from '@solana/wallet-adapter-react'
import { useCallback, useEffect, useState } from 'react'
import { twMerge } from 'tailwind-merge'
import { Page } from '~/components/Page'
import { EmptyBlock, ErrorBlock, Panel, SectionHeading, Spinner } from '~/components/ui'
import {
    buyListing,
    fetchInfo,
    fetchListings,
    TradeError,
    TradeInfo,
    TradeListing,
} from '../api/trade'
import { ListingCard } from '../components/ListingCard'
import { MyListings } from '../components/MyListings'
import { SellPanel } from '../components/SellPanel'

type Tab = 'browse' | 'sell' | 'mine'

const TABS: { key: Tab; label: string }[] = [
    { key: 'browse', label: 'Browse' },
    { key: 'sell', label: 'Sell' },
    { key: 'mine', label: 'My listings' },
]

const TYPES = [
    { value: '', label: 'All' },
    { value: 'ape', label: 'Apes' },
    { value: 'droid', label: 'Droids' },
    { value: 'weapon', label: 'Weapons' },
    { value: 'lootbox', label: 'Boxes' },
]

export const TradePage = () => {
    const { publicKey } = useWallet()
    const [tab, setTab] = useState<Tab>('browse')
    const [info, setInfo] = useState<TradeInfo | null>(null)
    const [listings, setListings] = useState<TradeListing[] | null>(null)
    const [loadError, setLoadError] = useState(false)
    const [type, setType] = useState('')
    const [search, setSearch] = useState('')
    const [buyingId, setBuyingId] = useState<number | null>(null)
    const [message, setMessage] = useState<string | null>(null)
    const [error, setError] = useState<string | null>(null)

    useEffect(() => {
        fetchInfo().then(setInfo).catch(() => undefined)
    }, [])

    const loadListings = useCallback(() => {
        setLoadError(false)
        setListings(null)
        fetchListings({ type: type || undefined, search: search || undefined })
            .then((page) => setListings(page.results))
            .catch(() => setLoadError(true))
    }, [type, search])

    useEffect(() => {
        if (tab === 'browse') loadListings()
    }, [tab, loadListings])

    const buy = async (listing: TradeListing) => {
        if (!publicKey) {
            setError('Connect a wallet first — it is where the NFT will be delivered.')
            return
        }
        setBuyingId(listing.id)
        setError(null)
        setMessage(null)

        try {
            await buyListing({
                listingId: listing.id,
                wallet: publicKey.toString(),
                // What this card displayed. The server refuses the purchase if
                // the listing has moved since, so a seller cannot reprice
                // between the render and the click.
                expectedPrice: listing.price,
                // One key per attempt. A double click or a retried request is
                // then one debit, not two.
                idempotencyKey: crypto.randomUUID(),
            })
            setMessage('Bought. The NFT is on its way to your wallet.')
            loadListings()
        } catch (e) {
            setError(e instanceof TradeError ? e.message : 'The purchase did not go through.')
        } finally {
            setBuyingId(null)
        }
    }

    return (
        <Page title='Marketplace' header='Marketplace'>
            <div className='mx-auto mb-16 flex w-full max-w-[1500px] flex-col gap-5 px-4 pt-5 md:mb-24 md:gap-6 md:pt-6'>
                {info && !info.escrow_available && (
                    <Panel className='p-4 text-sm text-[#FFC53D]' border='rgba(255,197,61,0.35)'>
                        The marketplace is not accepting new listings at the moment. Existing
                        listings are unaffected.
                    </Panel>
                )}

                <div className='flex flex-wrap items-center gap-2'>
                    {TABS.map((entry) => (
                        <button
                            key={entry.key}
                            onClick={() => {
                                setTab(entry.key)
                                setError(null)
                                setMessage(null)
                            }}
                            className={twMerge(
                                'border-b-2 px-4 py-2 text-xs font-black uppercase tracking-[0.2em] transition-colors',
                                tab === entry.key
                                    ? 'border-[#03CEFF] text-white'
                                    : 'border-transparent text-white/40 hover:text-white/70'
                            )}
                        >
                            {entry.label}
                        </button>
                    ))}
                    {info && (
                        <span className='ml-auto text-[11px] uppercase tracking-widest text-white/40'>
                            {info.fee_percent}% fee
                        </span>
                    )}
                </div>

                {message && <p className='text-xs text-[#00FF90]'>{message}</p>}
                {error && <p className='text-xs text-[#FF6B6B]'>{error}</p>}

                {tab === 'browse' && (
                    <>
                        <div className='flex flex-wrap items-center gap-2'>
                            {TYPES.map((entry) => (
                                <button
                                    key={entry.value}
                                    onClick={() => setType(entry.value)}
                                    className={twMerge(
                                        'border px-3 py-1.5 text-[11px] font-black uppercase tracking-widest transition-colors',
                                        type === entry.value
                                            ? 'border-[#03CEFF] text-white'
                                            : 'border-white/10 text-white/50 hover:border-white/25'
                                    )}
                                >
                                    {entry.label}
                                </button>
                            ))}
                            <input
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                placeholder='Search by name or mint'
                                className='ml-auto w-full max-w-xs border border-white/15 bg-black/40 px-3 py-1.5 text-sm text-white outline-none focus:border-[#03CEFF]'
                            />
                        </div>

                        {loadError && <ErrorBlock onRetry={loadListings} />}
                        {!loadError && listings === null && (
                            <div className='flex justify-center py-16'>
                                <Spinner visible={true} />
                            </div>
                        )}
                        {!loadError && listings?.length === 0 && (
                            <EmptyBlock title='Nothing listed yet'>
                                When someone lists an NFT it will show up here.
                            </EmptyBlock>
                        )}
                        {!loadError && listings && listings.length > 0 && (
                            <div className='grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5'>
                                {listings.map((listing) => (
                                    <ListingCard
                                        key={listing.id}
                                        listing={listing}
                                        onBuy={listing.is_mine ? undefined : buy}
                                        busy={buyingId === listing.id}
                                        disabled={buyingId !== null}
                                    />
                                ))}
                            </div>
                        )}
                    </>
                )}

                {tab === 'sell' && (
                    <>
                        <SectionHeading title='Sell an NFT' hint='Paid into your BR1 balance' />
                        <SellPanel info={info} onListed={() => setTab('mine')} />
                    </>
                )}

                {tab === 'mine' && (
                    <>
                        <SectionHeading title='My listings' />
                        <MyListings onChanged={loadListings} />
                    </>
                )}
            </div>
        </Page>
    )
}
